/**
 * Explainer video player (redesign spec §5.9).
 *
 * views/redesign/partials/explainer-video.ejs renders a poster and a play
 * button. Nothing is downloaded until the visitor presses play: then the
 * <video> (preload="none", captions track on by default) replaces the poster
 * and starts. Umami gets video_play, video_25, video_75 and video_complete,
 * each once per clip, with the clip id as a property (§10).
 */

import { trackMediaPlay } from '../utils/trackMediaPlay.js';

const MILESTONES = [[0.25, 'video_25'], [0.75, 'video_75']];

function track(name, clip) {
    try { window.umami?.track(name, { clip }); } catch { /* analytics is optional */ }
}

function buildVideo(player) {
    const video = document.createElement('video');
    video.controls = true;
    video.preload = 'none';
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    if (player.dataset.poster) video.poster = player.dataset.poster;
    video.src = player.dataset.src;

    if (player.dataset.captions) {
        const captions = document.createElement('track');
        captions.kind = 'captions';
        captions.srclang = 'en';
        captions.label = 'English';
        captions.src = player.dataset.captions;
        captions.default = true;
        video.appendChild(captions);
    }
    return video;
}

function watchProgress(video, clip) {
    const sent = new Set();
    const once = (name) => {
        if (sent.has(name)) return;
        sent.add(name);
        track(name, clip);
    };
    video.addEventListener('play', () => once('video_play'));
    video.addEventListener('timeupdate', () => {
        if (!(video.duration > 0)) return;
        const share = video.currentTime / video.duration;
        MILESTONES.forEach(([at, name]) => { if (share >= at) once(name); });
    });
    video.addEventListener('ended', () => once('video_complete'));
}

function start(player) {
    if (player.querySelector('video')) return;
    const clip = player.dataset.explainer;
    trackMediaPlay(`explainer:${clip}`);
    const video = buildVideo(player);
    watchProgress(video, clip);

    player.querySelector('.player__poster')?.remove();
    player.querySelector('.player__play')?.remove();
    player.querySelector('.player__caption')?.setAttribute('hidden', '');
    player.prepend(video);
    player.classList.add('is-playing');
    video.focus();

    const playing = video.play();
    if (playing && typeof playing.catch === 'function') {
        // Autoplay can still be refused (data saver, iOS low power); the
        // native controls are there, so the visitor just presses play again.
        playing.catch(() => {});
    }
}

export function initExplainerVideos(root = document) {
    root.querySelectorAll('.player[data-explainer][data-src]').forEach((player) => {
        const button = player.querySelector('.player__play');
        if (!button || button.dataset.bound) return;
        button.dataset.bound = 'true';
        button.addEventListener('click', () => start(player));
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initExplainerVideos());
} else {
    initExplainerVideos();
}
